import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import LocalGasStationOutlinedIcon from '@mui/icons-material/LocalGasStationOutlined';
import SpaOutlinedIcon from '@mui/icons-material/SpaOutlined';
import { ModuleColumn } from '../types';
import { brandColors } from '../theme/appTheme';
import {
  masterVehicleName,
  ownershipLabel,
  resolveVehicleTypePreset,
  vehicleTypeIcon,
} from './vehicleTypePresets';

const statusStyle = (status: string) => {
  const s = status.toLowerCase();
  if (s === 'active') return { label: 'Active', bg: '#dcfce7', color: '#166534', dot: '#16a34a' };
  if (s === 'maintenance') return { label: 'Maintenance', bg: '#ffedd5', color: '#c2410c', dot: '#ea580c' };
  if (s === 'inactive') return { label: 'Inactive', bg: '#fee2e2', color: '#b91c1c', dot: '#dc2626' };
  return { label: status || '—', bg: '#f1f5f9', color: '#475569', dot: '#94a3b8' };
};

const fuelIcon = (fuel: string) => {
  const f = fuel.toLowerCase();
  if (f.includes('cng') || f.includes('lng') || f.includes('electric') || f.includes('ev')) {
    return <SpaOutlinedIcon sx={{ fontSize: 14, color: '#15803d' }} />;
  }
  return <LocalGasStationOutlinedIcon sx={{ fontSize: 14, color: '#1d4ed8' }} />;
};

/** Vehicles list — General Information fields + 5 type color presets. */
export const vehicleTableColumns: ModuleColumn[] = [
  {
    field: 'vehicleNumber',
    header: 'Vehicle No.',
    searchValue: (row) => String(row.vehicleNumber || ''),
    render: (row) => (
      <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: brandColors.textDark, letterSpacing: '0.02em' }}>
        {String(row.vehicleNumber || '—')}
      </Typography>
    ),
  },
  {
    field: 'vehicleType',
    header: 'Type',
    searchValue: (row) => masterVehicleName(row, ['vehicleType', 'typeName']),
    exportValue: (row) => masterVehicleName(row, ['vehicleType', 'typeName']),
    render: (row) => {
      const name = masterVehicleName(row, ['vehicleType', 'typeName']);
      if (name === '—') return '—';
      const preset = resolveVehicleTypePreset(name);
      return (
        <Chip
          size="small"
          icon={<Box sx={{ display: 'flex', color: preset.color, ml: 0.75 }}>{vehicleTypeIcon(preset)}</Box>}
          label={name}
          sx={{
            bgcolor: preset.bg,
            color: preset.color,
            fontWeight: 700,
            fontSize: 11,
            height: 24,
            borderRadius: '999px',
            '& .MuiChip-icon': { color: preset.color },
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      );
    },
  },
  {
    field: 'brand',
    header: 'Make',
    searchValue: (row) => masterVehicleName(row, ['brand', 'makeName']),
    exportValue: (row) => masterVehicleName(row, ['brand', 'makeName']),
    render: (row) => masterVehicleName(row, ['brand', 'makeName']),
  },
  {
    field: 'vehicleModel',
    header: 'Model',
    searchValue: (row) => masterVehicleName(row, ['vehicleModel', 'modelName']),
    exportValue: (row) => masterVehicleName(row, ['vehicleModel', 'modelName']),
    render: (row) => masterVehicleName(row, ['vehicleModel', 'modelName']),
  },
  {
    field: 'year',
    header: 'Year',
    searchValue: (row) => String(row.year || ''),
    render: (row) => String(row.year || '—'),
  },
  {
    field: 'capacity',
    header: 'Capacity',
    format: 'number',
    searchValue: (row) => String(row.capacity ?? ''),
    exportValue: (row) => (row.capacity != null && row.capacity !== '' ? `${row.capacity} kg` : ''),
    render: (row) => {
      const n = Number(row.capacity);
      if (!Number.isFinite(n) || n <= 0) return '—';
      return (
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: brandColors.textDark }}>
          {n.toLocaleString('en-IN')} kg
        </Typography>
      );
    },
  },
  {
    field: 'fuelType',
    header: 'Fuel',
    searchValue: (row) => String(row.fuelType || ''),
    render: (row) => {
      const fuel = String(row.fuelType || '').trim();
      if (!fuel) return '—';
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          {fuelIcon(fuel)}
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: brandColors.textDark }}>{fuel}</Typography>
        </Box>
      );
    },
  },
  {
    field: 'maxSpeed',
    header: 'Max Speed',
    format: 'number',
    searchValue: (row) => String(row.maxSpeed ?? ''),
    exportValue: (row) => (row.maxSpeed != null && row.maxSpeed !== '' ? `${row.maxSpeed} km/h` : ''),
    render: (row) => {
      const n = Number(row.maxSpeed);
      if (!Number.isFinite(n) || n <= 0) return '—';
      return `${n} km/h`;
    },
  },
  {
    field: 'clientName',
    header: 'Client',
    searchValue: (row) => String(row.clientName || ''),
    render: (row) => String(row.clientName || '—'),
  },
  {
    field: 'ownerType',
    header: 'Ownership',
    searchValue: (row) => ownershipLabel(row),
    exportValue: (row) => ownershipLabel(row),
    render: (row) => {
      const leased = ['leased', 'rented'].includes(String(row.ownerType || '').toLowerCase());
      return (
        <Chip
          size="small"
          label={ownershipLabel(row)}
          sx={{
            bgcolor: leased ? '#ede9fe' : '#e0e7ff',
            color: leased ? '#6d28d9' : '#3730a3',
            fontWeight: 700,
            fontSize: 11,
            height: 22,
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      );
    },
  },
  {
    field: 'status',
    header: 'Status',
    searchValue: (row) => statusStyle(String(row.status || '')).label,
    exportValue: (row) => statusStyle(String(row.status || '')).label,
    render: (row) => {
      const style = statusStyle(String(row.status || ''));
      return (
        <Chip
          size="small"
          label={
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: style.dot }} />
              {style.label}
            </Box>
          }
          sx={{
            bgcolor: style.bg,
            color: style.color,
            fontWeight: 700,
            fontSize: 11,
            height: 22,
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      );
    },
  },
];
